import { useState, useEffect } from "react";
import { FiX, FiPlus, FiTrash2 } from "react-icons/fi";

const OtherChargesDrawer = ({
  isOpen = false,
  onClose = () => {},
  onSave = () => {},
  initialCharges = [],
  isFullWidth=false,
  drawerWidth=null,
}) => {
  const [charges, setCharges] = useState([]);

  // Simple scroll prevention
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }

    return () => {
      document.body.style.overflow = "";
    };
  }, [isOpen]);

  useEffect(() => {
    if (isOpen) {
      setCharges(
        initialCharges.length > 0
          ? initialCharges
          : [{ description: "", amount: "" }]
      );
    }
  }, [isOpen, initialCharges]);

  const handleAddCharge = () => {
    setCharges([...charges, { description: "", amount: "" }]);
  };

  const handleRemoveCharge = (index) => {
    if (charges.length > 1) {
      const updatedCharges = charges.filter((_, i) => i !== index);
      setCharges(updatedCharges);
    }
  };

  const handleChargeChange = (index, field, value) => {
    const updatedCharges = [...charges];
    updatedCharges[index][field] = value;
    setCharges(updatedCharges);
  };

  const handleSave = () => {
    // Filter out empty charges
    const validCharges = charges
      .filter((charge) => charge.description.trim() && charge.amount)
      .map((charge) => ({
        ...charge,
        amount: parseFloat(charge.amount) || 0,
      }));

    onSave(validCharges);
    onClose();
  };

  const calculateTotal = () => {
    return charges.reduce((total, charge) => {
      const amount = parseFloat(charge.amount) || 0;
      return total + amount;
    }, 0);
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-end bg-black bg-opacity-40">
      <div className={`relative w-full h-full bg-white shadow-xl ${!isFullWidth ? drawerWidth || "lg:w-2/5": '' } `}>
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b bg-gradient-to-r from-blue-50 to-indigo-50">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 bg-gradient-to-r from-blue-500 to-indigo-600 rounded-full flex items-center justify-center">
              <FiPlus className="w-4 h-4 text-white" />
            </div>
            <h2 className="text-xl font-semibold text-gray-800">
              Other Charges
            </h2>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-full transition-colors"
          >
            <FiX className="w-5 h-5 text-gray-600" />
          </button>
        </div>

        {/* Content */}
        <div className="p-4 overflow-y-auto h-[calc(100vh-200px)]">
          <div className="mb-4">
            <p className="text-sm text-gray-600 mb-4">
              Add additional charges such as late fees, damage deposits, special
              requests, etc.
            </p>

            <div className="space-y-4">
              {charges.map((charge, index) => (
                <div
                  key={index}
                  className="p-4 border border-gray-200 rounded-lg bg-gray-50"
                >
                  <div className="flex justify-between items-center mb-3">
                    <span className="text-sm font-medium text-gray-700">
                      Charge #{index + 1}
                    </span>
                    {charges.length > 1 && (
                      <button
                        onClick={() => handleRemoveCharge(index)}
                        className="p-1 hover:bg-red-50 rounded text-red-500 hover:text-red-700"
                      >
                        <FiTrash2 className="w-4 h-4" />
                      </button>
                    )}
                  </div>

                  <div className="space-y-3">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Description *
                      </label>
                      <input
                        type="text"
                        value={charge.description}
                        onChange={(e) =>
                          handleChargeChange(
                            index,
                            "description",
                            e.target.value
                          )
                        }
                        placeholder="E.g., Late pickup fee, Damage deposit, Special handling, etc."
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Amount (₹) *
                      </label>
                      <input
                        type="number"
                        value={charge.amount}
                        onChange={(e) =>
                          handleChargeChange(index, "amount", e.target.value)
                        }
                        placeholder="0.00"
                        min="0"
                        step="0.01"
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      />
                    </div>
                  </div>
                </div>
              ))}
            </div>

            <button
              onClick={handleAddCharge}
              className="mt-4 w-full py-2 px-4 border-2 border-dashed border-gray-300 rounded-lg text-gray-600 hover:border-gray-400 hover:text-gray-700 hover:bg-gray-50 transition-colors flex items-center justify-center gap-2"
            >
              <FiPlus className="w-4 h-4" />
              Add Another Charge
            </button>
          </div>

          {/* Total Preview */}
          <div className="mt-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
            <div className="flex justify-between items-center">
              <span className="text-sm font-medium text-gray-700">
                Total Other Charges:
              </span>
              <span className="text-lg font-bold text-blue-700">
                ₹{calculateTotal().toFixed(2)}
              </span>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="absolute bottom-0 left-0 right-0 p-6 border-t bg-white">
          <div className="flex gap-3">
            <button
              onClick={onClose}
              className="flex-1 py-3 px-4 border border-gray-300 text-gray-700 font-semibold rounded-lg hover:bg-gray-50 transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={handleSave}
              className="flex-1 py-3 px-4 bg-gradient-to-r from-blue-600 to-indigo-600 text-white font-semibold rounded-lg hover:from-blue-700 hover:to-indigo-700 transition-colors"
            >
              Save Charges
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default OtherChargesDrawer;
